import React, { useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Download,
  Search,
  XCircle,
} from "lucide-react";
import { useTrading } from "../../context/TradingContext";

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value || 0);

const normalizeTransactionStatus = (status, transaction) => {
  const normalizedStatus = String(status || "").toLowerCase();
  const isWithdrawal =
    String(transaction?.transactionType || "").toLowerCase() === "withdrawal" ||
    String(transaction?.side || "").toLowerCase() === "withdrawal" ||
    String(transaction?.type || "").toLowerCase() === "withdrawal";

  if (!isWithdrawal) {
    return normalizedStatus;
  }

  if (normalizedStatus === "request accepted") {
    return "pending";
  }

  return normalizedStatus;
};

const isWithdrawalTransaction = (transaction) =>
  String(transaction?.transactionType || "").toLowerCase() === "withdrawal" ||
  String(transaction?.side || "").toLowerCase() === "withdrawal" ||
  String(transaction?.type || "").toLowerCase() === "withdrawal";

const getWithdrawalStatusRank = (status) => {
  const normalizedStatus = normalizeTransactionStatus(status);

  if (normalizedStatus === "approved") {
    return 3;
  }

  if (normalizedStatus === "failed" || normalizedStatus === "rejected") {
    return 2;
  }

  if (normalizedStatus === "pending") {
    return 1;
  }

  return 0;
};

const getWithdrawalTransactionSignature = (transaction) =>
  [
    String(transaction?.symbol || "").toUpperCase(),
    String(transaction?.network || transaction?.source || "").toUpperCase(),
    String(transaction?.address || "").trim().toLowerCase(),
    Number(transaction?.amount ?? transaction?.price ?? 0).toFixed(2),
  ].join(":");

const dedupeWithdrawalTransactions = (transactions) => {
  const result = [];
  const withdrawalIndexBySignature = new Map();

  transactions.forEach((transaction) => {
    if (!isWithdrawalTransaction(transaction)) {
      result.push(transaction);
      return;
    }

    const signature = getWithdrawalTransactionSignature(transaction);
    const existingIndex = withdrawalIndexBySignature.get(signature);

    if (existingIndex === undefined) {
      withdrawalIndexBySignature.set(signature, result.length);
      result.push(transaction);
      return;
    }

    const existingTransaction = result[existingIndex];
    const existingRank = getWithdrawalStatusRank(existingTransaction.status);
    const nextRank = getWithdrawalStatusRank(transaction.status);
    const existingTimestamp = new Date(existingTransaction.timestamp || 0).getTime();
    const nextTimestamp = new Date(transaction.timestamp || 0).getTime();

    if (
      nextRank > existingRank ||
      (nextRank === existingRank && nextTimestamp >= existingTimestamp)
    ) {
      result[existingIndex] = {
        ...existingTransaction,
        ...transaction,
        ...(nextRank >= existingRank ? { status: transaction.status } : {}),
        timestamp:
          nextTimestamp >= existingTimestamp
            ? transaction.timestamp
            : existingTransaction.timestamp,
      };
    }
  });

  return result;
};

const getStatusMeta = (status, transaction) => {
  const normalizedStatus = normalizeTransactionStatus(status, transaction);
  const isWithdrawal =
    String(transaction?.transactionType || "").toLowerCase() === "withdrawal" ||
    String(transaction?.side || "").toLowerCase() === "withdrawal" ||
    String(transaction?.type || "").toLowerCase() === "withdrawal";

  if (isWithdrawal && normalizedStatus === "pending") {
    return {
      className: "bg-fuchsia-500/10 text-fuchsia-300 ring-1 ring-fuchsia-400/20",
      icon: Clock3,
      label: "Pending",
    };
  }

  if (isWithdrawal && normalizedStatus === "approved") {
    return {
      className: "bg-emerald-500/10 text-emerald-300",
      icon: CheckCircle2,
      label: "Approved",
    };
  }

  if (normalizedStatus === "successful" || normalizedStatus === "completed") {
    return {
      className: "bg-emerald-500/10 text-emerald-300",
      icon: CheckCircle2,
      label: "Successful",
    };
  }

  if (normalizedStatus === "failed" || (isWithdrawal && normalizedStatus === "rejected")) {
    return {
      className: "bg-rose-500/10 text-rose-300",
      icon: XCircle,
      label: "Failed",
    };
  }

  return {
    className: "bg-amber-500/10 text-amber-300",
    icon: Clock3,
    label: "Pending",
  };
};

const TransactionHistory = ({ compact = false }) => {
  const { transactions } = useTrading();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      const matchesSearch =
        !search ||
        transaction.symbol?.toLowerCase().includes(search.toLowerCase()) ||
        transaction.id?.toLowerCase().includes(search.toLowerCase()) ||
        transaction.source?.toLowerCase().includes(search.toLowerCase()) ||
        transaction.description?.toLowerCase().includes(search.toLowerCase()) ||
        transaction.network?.toLowerCase().includes(search.toLowerCase()) ||
        transaction.address?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        status === "all" ||
        normalizeTransactionStatus(transaction.status, transaction) === status;
      return matchesSearch && matchesStatus;
    });
  }, [transactions, search, status]);

  const rows = useMemo(() => {
    const dedupedTransactions = dedupeWithdrawalTransactions(filteredTransactions);
    return compact ? dedupedTransactions.slice(0, 6) : dedupedTransactions;
  }, [compact, filteredTransactions]);

  const exportTransactions = () => {
    const payload = rows.map((transaction) => ({
      id: transaction.id,
      symbol: transaction.symbol,
      side: transaction.side,
      status: transaction.status,
      quantity: transaction.quantity,
      price: transaction.price,
      profit: transaction.profit || 0,
      timestamp: transaction.timestamp,
    }));

    const csv = [
      Object.keys(payload[0] || { id: "", symbol: "", side: "", status: "", quantity: "", price: "", profit: "", timestamp: "" }).join(","),
      ...payload.map((row) => Object.values(row).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "trade-history.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-3xl border border-slate-800 bg-slate-950/80">
      <div className="border-b border-slate-800 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Transaction History</h3>
            <p className="text-sm text-slate-400">
              Records account activity in real time and keeps a rolling 30-day history.
            </p>
          </div>
          <button
            onClick={exportTransactions}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 transition hover:border-slate-500"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>

        {!compact && (
          <div className="mt-4 grid gap-3 md:grid-cols-[1fr_180px]">
            <label className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by symbol or id"
                className="w-full rounded-xl border border-slate-800 bg-slate-900 px-10 py-2.5 text-sm text-slate-100 outline-none transition focus:border-cyan-400"
              />
            </label>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="rounded-xl border border-slate-800 bg-slate-900 px-3 py-2.5 text-sm text-slate-100 outline-none transition focus:border-cyan-400"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        )}
      </div>

      <div className="md:hidden">
        <div className="space-y-3 p-3">
          {rows.map((transaction) => {
            const isWithdrawal =
              String(transaction?.transactionType || "").toLowerCase() === "withdrawal" ||
              String(transaction?.side || "").toLowerCase() === "withdrawal" ||
              String(transaction?.type || "").toLowerCase() === "withdrawal";
            const withdrawalDisplayValue = Number(
              transaction.amount ?? transaction.profit ?? transaction.price ?? 0
            );
            const displayedQuantity = isWithdrawal
              ? "1"
              : transaction.quantity?.toFixed?.(5) || "-";
            const displayedPrice = isWithdrawal
              ? formatMoney(withdrawalDisplayValue)
              : formatMoney(transaction.price);
            const displayedProfitValue = isWithdrawal
              ? withdrawalDisplayValue
              : transaction.profit || 0;
            const statusMeta = getStatusMeta(transaction.status, transaction);
            const StatusIcon = statusMeta.icon;

            return (
              <div
                key={transaction.id}
                className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-100">
                      {transaction.symbol || "Trade Event"}
                    </div>
                    <div className="mt-1 break-words text-xs text-slate-500">
                      {transaction.description || transaction.id}
                    </div>
                    {transaction.network && (
                      <div className="mt-1 text-xs text-slate-500">{transaction.network}</div>
                    )}
                    {transaction.address && (
                      <div className="mt-1 break-all text-xs text-slate-600">
                        {transaction.address}
                      </div>
                    )}
                  </div>
                  <span
                    className={`inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium ${statusMeta.className}`}
                  >
                    <StatusIcon className="h-3.5 w-3.5" />
                    {statusMeta.label}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                      Side
                    </div>
                    <div className="mt-1 break-words text-slate-200 uppercase">
                      {transaction.side || "system"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                      Qty
                    </div>
                    <div className="mt-1 break-words text-slate-200">{displayedQuantity}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                      Price
                    </div>
                    <div className="mt-1 break-words text-slate-200">{displayedPrice}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                      Profit
                    </div>
                    <div
                      className={`mt-1 break-words ${
                        displayedProfitValue > 0
                          ? "text-emerald-400"
                          : displayedProfitValue < 0
                          ? "text-rose-400"
                          : "text-slate-400"
                      }`}
                    >
                      {formatMoney(displayedProfitValue)}
                    </div>
                  </div>
                </div>

                <div className="mt-4 border-t border-slate-800 pt-3 text-xs text-slate-500">
                  {new Date(transaction.timestamp).toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {rows.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 px-6 pb-6 text-center md:hidden">
          <AlertCircle className="h-10 w-10 text-slate-600" />
          <div className="text-white">No transactions yet</div>
          <p className="max-w-md text-sm text-slate-500">
            History stays empty until a trade or wallet transaction is recorded.
          </p>
        </div>
      )}

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-slate-800 text-left text-slate-500">
              <th className="px-5 py-3 font-medium">Transaction</th>
              <th className="px-5 py-3 font-medium">Side</th>
              <th className="px-5 py-3 font-medium text-right">Qty</th>
              <th className="px-5 py-3 font-medium text-right">Price</th>
              <th className="px-5 py-3 font-medium text-right">Profit</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium text-right">Time</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((transaction) => {
              const isWithdrawal =
                String(transaction?.transactionType || "").toLowerCase() === "withdrawal" ||
                String(transaction?.side || "").toLowerCase() === "withdrawal" ||
                String(transaction?.type || "").toLowerCase() === "withdrawal";
              const withdrawalDisplayValue = Number(
                transaction.amount ?? transaction.profit ?? transaction.price ?? 0
              );
              const displayedQuantity = isWithdrawal
                ? "1"
                : transaction.quantity?.toFixed?.(5) || "-";
              const displayedPrice = isWithdrawal
                ? formatMoney(withdrawalDisplayValue)
                : formatMoney(transaction.price);
              const displayedProfitValue = isWithdrawal
                ? withdrawalDisplayValue
                : transaction.profit || 0;
              const statusMeta = getStatusMeta(transaction.status, transaction);
              const StatusIcon = statusMeta.icon;

              return (
                <tr key={transaction.id} className="border-b border-slate-900/80 text-slate-200">
                  <td className="px-5 py-4">
                    <div className="font-medium">{transaction.symbol || "Trade Event"}</div>
                    <div className="text-xs text-slate-500">{transaction.description || transaction.id}</div>
                    {transaction.network && (
                      <div className="mt-1 text-xs text-slate-500">
                        {transaction.network}
                      </div>
                    )}
                    {transaction.address && (
                      <div className="mt-1 max-w-[280px] truncate text-xs text-slate-600">
                        {transaction.address}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 uppercase text-slate-300">{transaction.side || "system"}</td>
                  <td className="px-5 py-4 text-right">{displayedQuantity}</td>
                  <td className="px-5 py-4 text-right">{displayedPrice}</td>
                  <td
                    className={`px-5 py-4 text-right ${
                      displayedProfitValue > 0
                        ? "text-emerald-400"
                        : displayedProfitValue < 0
                        ? "text-rose-400"
                        : "text-slate-400"
                    }`}
                  >
                    {formatMoney(displayedProfitValue)}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${statusMeta.className}`}
                    >
                      <StatusIcon className="h-3.5 w-3.5" />
                      {statusMeta.label}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right text-slate-500">
                    {new Date(transaction.timestamp).toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {rows.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
            <AlertCircle className="h-10 w-10 text-slate-600" />
            <div className="text-white">No transactions yet</div>
            <p className="max-w-md text-sm text-slate-500">
              History stays empty until a trade or wallet transaction is recorded.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
